import { useState, useEffect } from "react";
import { INCIDENT_TIPS } from "@/mocks/gameData";
import type { GameEvent } from "@/hooks/useGameState";

interface Props {
  events: GameEvent[];
  currentSquare: number;
  turn: number;
  correctCards: number;
  rankLabel: string;
  rankColor: string;
  rankIcon: string;
}

function SystemMetric({ label, value, unit, color }: { label: string; value: number; unit: string; color: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-white/[0.04] last:border-0">
      <span className="text-[11px] text-white/35 font-mono">{label}</span>
      <span className="text-[11px] font-mono font-bold" style={{ color }}>
        {value}{unit}
      </span>
    </div>
  );
}

export default function SidePanel({ events, currentSquare, turn, correctCards, rankLabel, rankColor, rankIcon }: Props) {
  const [tipIndex, setTipIndex] = useState(0);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const tipInterval = setInterval(() => {
      setTipIndex((prev) => (prev + 1) % INCIDENT_TIPS.length);
    }, 9000);
    const clockInterval = setInterval(() => setTime(new Date()), 1000);
    return () => {
      clearInterval(tipInterval);
      clearInterval(clockInterval);
    };
  }, []);

  const getEventColor = (type: GameEvent["type"]) => {
    switch (type) {
      case "snake": return "#ff4444";
      case "ladder": return "#00ff88";
      case "card": return "#00d9ff";
      case "win": return "#f59e0b";
      default: return "rgba(255,255,255,0.3)";
    }
  };

  const getEventIcon = (type: GameEvent["type"]) => {
    switch (type) {
      case "snake": return "ri-arrow-down-line";
      case "ladder": return "ri-arrow-up-line";
      case "card": return "ri-file-warning-line";
      case "win": return "ri-trophy-line";
      case "start": return "ri-play-circle-line";
      default: return "ri-arrow-right-s-line";
    }
  };

  const getEventLabel = (type: GameEvent["type"]) => {
    switch (type) {
      case "snake": return "FAILURE";
      case "ladder": return "WIN";
      case "card": return "INCIDENT";
      case "win": return "COMPLETE";
      case "start": return "INIT";
      default: return "MOVE";
    }
  };

  const progress = Math.min(100, Math.round((currentSquare / 100) * 100));
  const latestEvent = events[0];

  const timeStr = `${String(time.getHours()).padStart(2, "0")}:${String(time.getMinutes()).padStart(2, "0")}:${String(time.getSeconds()).padStart(2, "0")}`;

  return (
    <div className="flex flex-col gap-2 h-full">

      {/* System clock / header */}
      <div
        className="rounded-lg px-3 py-2 flex items-center justify-between flex-shrink-0"
        style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse flex-shrink-0"></span>
          <span className="text-[10px] text-white/30 font-mono uppercase tracking-widest">SRE Dashboard</span>
        </div>
        <span className="text-[11px] text-[#00ff88]/60 font-mono tabular-nums">{timeStr}</span>
      </div>

      {/* Current position */}
      <div
        className="rounded-lg p-3 flex-shrink-0"
        style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest mb-2">Current Position</div>
        <div className="flex items-end gap-3 mb-3">
          <div>
            <div className="text-4xl font-black font-mono text-white leading-none">{currentSquare}</div>
            <div className="text-[10px] text-white/25 font-mono mt-0.5">of 100 squares</div>
          </div>
          <div className="flex-1 pb-0.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-white/25 font-mono">Progress</span>
              <span className="text-[10px] font-mono font-bold" style={{ color: rankColor }}>{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, #00ff88, ${rankColor})`,
                  boxShadow: `0 0 6px ${rankColor}60`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Rank */}
        <div
          className="flex items-center gap-2 p-2 rounded-md"
          style={{ background: `${rankColor}08`, border: `1px solid ${rankColor}20` }}
        >
          <div
            className="w-6 h-6 flex items-center justify-center rounded-full border flex-shrink-0"
            style={{ borderColor: `${rankColor}50`, background: `${rankColor}15` }}
          >
            <i className={`${rankIcon} text-[10px]`} style={{ color: rankColor }}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] text-white/30 font-mono uppercase tracking-wider">Current Rank</div>
            <div className="text-[11px] font-bold text-white truncate">{rankLabel}</div>
          </div>
          <div
            className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded flex-shrink-0"
            style={{ background: `${rankColor}20`, color: rankColor }}
          >
            ACTIVE
          </div>
        </div>
      </div>

      {/* System metrics */}
      <div
        className="rounded-lg p-3 flex-shrink-0"
        style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest mb-1.5">Session Metrics</div>
        <SystemMetric label="turns_elapsed" value={turn} unit="" color="rgba(255,255,255,0.6)" />
        <SystemMetric label="cards_correct" value={correctCards} unit="" color="#00ff88" />
        <SystemMetric label="squares_remaining" value={Math.max(0, 100 - currentSquare)} unit="" color="#f59e0b" />
        <SystemMetric label="completion_pct" value={progress} unit="%" color={rankColor} />
      </div>

      {/* Latest event */}
      {latestEvent && (
        <div
          className="rounded-lg p-3 flex-shrink-0"
          style={{
            background: "#0e0e0e",
            border: `1px solid ${getEventColor(latestEvent.type)}25`,
          }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest">Latest Event</div>
            <span
              className="text-[9px] font-bold font-mono px-1.5 py-0.5 rounded"
              style={{
                background: `${getEventColor(latestEvent.type)}15`,
                color: getEventColor(latestEvent.type),
              }}
            >
              {getEventLabel(latestEvent.type)}
            </span>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center mt-0.5">
              <i
                className={`${getEventIcon(latestEvent.type)} text-xs`}
                style={{ color: getEventColor(latestEvent.type) }}
              ></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-white/55 leading-relaxed font-mono">{latestEvent.message}</p>
              <span className="text-[9px] text-white/20 font-mono">{latestEvent.timestamp}</span>
            </div>
          </div>
        </div>
      )}

      {/* Activity log */}
      <div
        className="rounded-lg p-3 flex-shrink-0"
        style={{ background: "#0e0e0e", border: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00ff88] animate-pulse flex-shrink-0"></span>
          <div className="text-[10px] text-white/25 font-mono uppercase tracking-widest">Event Log</div>
        </div>
        <div className="flex flex-col gap-0 overflow-y-auto max-h-32">
          {events.slice(0, 12).map((evt, i) => (
            <div
              key={evt.id}
              className="flex items-start gap-2 py-1 border-b border-white/[0.03] last:border-0"
              style={{ opacity: i === 0 ? 1 : Math.max(0.3, 1 - i * 0.08) }}
            >
              <span
                className="flex-shrink-0 text-[9px] font-bold font-mono mt-0.5 w-11 text-right"
                style={{ color: getEventColor(evt.type) }}
              >
                {getEventLabel(evt.type)}
              </span>
              <p className="flex-1 text-[10px] text-white/40 leading-relaxed font-mono">{evt.message}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Incident tip */}
      <div
        className="rounded-lg p-3 flex-shrink-0"
        style={{ background: "rgba(0,255,136,0.04)", border: "1px solid rgba(0,255,136,0.15)" }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <i className="ri-terminal-box-line text-[#00ff88] text-xs flex-shrink-0"></i>
          <div className="text-[10px] text-[#00ff88]/70 font-mono uppercase tracking-widest font-bold">SRE Insight</div>
        </div>
        <p className="text-[10px] text-white/50 leading-relaxed font-mono">{INCIDENT_TIPS[tipIndex]}</p>
        <button
          onClick={() => setTipIndex((prev) => (prev + 1) % INCIDENT_TIPS.length)}
          className="mt-1.5 text-[10px] text-[#00ff88]/40 hover:text-[#00ff88]/70 transition-colors font-mono cursor-pointer"
        >
          $ next_insight --random
        </button>
      </div>
    </div>
  );
}
